<template>
  <div class="RMPE-CustomForm DynamicForm">
    <transition
      :name="formState.animationDirection"
      mode="out-in"
    >
      <component
        :is="resolveField(currentQuestion)"
        :key="currentQuestion.id"
        v-model="form[currentQuestion.id]"
        :question="currentQuestion"
        :error="validateStatus({ field: $v.form[currentQuestion.id], serverKey: currentQuestion.id, label: currentQuestion.label })"
        :auto-focus-first-field="autoFocusFirstField"
        @change="validateBlur({ field: $v.form[currentQuestion.id], serverKey: currentQuestion.id })"
        @go-to-next-question="handleNextQuestionNavigation"
      />
    </transition>
    <div
      v-if="!isCurrentQuestionTheFirstQuestion(currentQuestion)"
      class="DynamicForm-ResetButton"
    >
      <button
        class="RMP-DynamicForm-ResetButton button is-primary is-small"
        @click="goBackToFirstQuestion"
      >
        <span class="icon is-small is-right">
          <font-awesome-icon :icon="icon" />
        </span>
      </button>
    </div>
    <debug-controls
      v-if="debugMode && false"
      :current-question="formState.currentQuestion"
      :questions-length="filteredQuestions.length"
      @debug-controls:change-question="(newQuestion) => { formState.currentQuestion > newQuestion ? formState.animationDirection = 'previous': formState.animationDirection = 'next'; formState.currentQuestion = newQuestion;}"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  required, email, decimal, url,
} from 'vuelidate/lib/validators';
import template from 'lodash/template';
import isEmpty from 'lodash/isEmpty';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import {
  fullName, maxUploadedFileSizeValidator, mimeTypeUploadedFileValidator,
} from '../validators';
import CheckboxField from '../fields/dynamic/checkbox-field.vue';
import CheckboxGroupField from '../fields/dynamic/checkbox-group-field.vue';
import EmailField from '../fields/dynamic/email-field.vue';
import LabelField from '../fields/dynamic/label-field.vue';
import RadioField from '../fields/dynamic/radio-field.vue';
import SelectField from '../fields/dynamic/select-field.vue';
import TextField from '../fields/dynamic/text-field.vue';
import NumberField from '../fields/dynamic/number-field.vue';
import TelephoneField from '../fields/dynamic/telephone-field.vue';
import TextareaField from '../fields/dynamic/textarea-field.vue';
import AutocompleteField from '../fields/dynamic/autocomplete-field.vue';
import DateField from '../fields/dynamic/date-field.vue';
import TimeField from '../fields/dynamic/time-field.vue';
import RatingField from '../fields/dynamic/rating-field.vue';
import MultiChoiceImageField from '../fields/dynamic/multi-choice-image-field.vue';
import StatementField from '../fields/dynamic/statement-field.vue';
import UrlField from '../fields/dynamic/url-field.vue';
import RankingField from '../fields/dynamic/ranking-field.vue';
import FileUploadField from '../fields/dynamic/file-upload-field.vue';
import ValidationMixin from '../mixins/validation-mixin';
import DebugControls from '../partials/debug-controls.vue';
import Lang from '../lang/index';

export default {
  components: {
    DebugControls, FontAwesomeIcon,
  },

  mixins: [ValidationMixin],

  props: {
    questions: {
      type: Array,
      default: () => [],
    },
    config: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    autoFocusFirstField: {
      type: Boolean,
      default: false,
    },
    debugMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const queryParams = new URLSearchParams(window.location.search);

    const formStructure = this.questions
      .filter((question) => question.type !== 'label' && question.type !== 'statement')
      .reduce((carry, question) => {
        carry[question.id] = this.preFillQuestion(queryParams, question);
        return carry;
      }, {});

    return {
      form: formStructure,
      icon: faArrowUp,
      formState: {
        loading: false,
        currentQuestion: 0,
        animationDirection: '',
      },
      serverValidationErrors: {},
    };
  },

  computed: {
    lang() {
      return Lang;
    },
    /**
     * Questions that have a set of possible answers can have a score associated which answer.
     * The idea here is to:
     *  - lets get all the questions that have been answered
     *  - lets get all questions that have a score configuration
     *  - lets see what score the question answer has
     *
     * The end result is the overall current score.
     *
     * @returns int
     */
    currentScore() {
      const questionThatHaveBeenAnswered = Object.keys(this.form)
        .filter((field) => {
          return this.form[field] !== null;
        });

      const questionsWithScoreConfiguration = this.questions.filter((question) => {
        return question.scores !== undefined && question.scores.length > 0;
      });

      // The loop base is the questions with a score configuration because they are likely less then answered questions
      return questionsWithScoreConfiguration.reduce((accumulator, singleQuestionScoreConfiguration) => {
        const questionId = singleQuestionScoreConfiguration.id;

        const answerHasScoreAssociated = questionThatHaveBeenAnswered.indexOf(questionId) > -1;

        if (!answerHasScoreAssociated) {
          return accumulator;
        }

        const answer = this.form[questionId];

        const possibleScores = singleQuestionScoreConfiguration.scores;

        // eslint-disable-next-line no-plusplus
        for (let index = 0; index < possibleScores.length; index++) {
          if (possibleScores[index].value === answer) {
            accumulator += possibleScores[index].score;
            break;
          }
        }

        return accumulator;
      }, 0);
    },
    currentQuestion() {
      const currentQ = this.filteredQuestions[this.formState.currentQuestion];
      const labelTemplate = template(currentQ.label);
      const descriptionTemplate = template(currentQ.warningMessage);

      const reformattedFormData = Object.keys(this.form)
        .map((formKey) => {
          const foundQuestion = this.filteredQuestions.find((question) => question.id === formKey);

          let questionNumber = 'XXX';

          if (foundQuestion !== undefined) {
            questionNumber = foundQuestion.questionNumber;
          }

          return {
            key: questionNumber,
            value: this.form[formKey],
          };
        }).reduce((acc, formField) => {
          acc[formField.key] = formField.value;
          return acc;
        }, { currentScore: this.currentScore });

      // console.log(reformattedFormData);

      // We need to set the initialValue because of pre-filling and the fact that some questions
      // fire events that are only triggered if they the question has an initialVal (eg: dob).
      // Though only replace the default value with the pre-fill if there is any.

      if (typeof this.form[currentQ.id] === 'string' && !this.isVariableEmpty(this.form[currentQ.id])) {
        currentQ.initialVal = this.form[currentQ.id];
      }

      try {
        currentQ.formattedLabel = labelTemplate(reformattedFormData);
      } catch {
        currentQ.formattedLabel = currentQ.label;
      }

      try {
        currentQ.formattedDescription = descriptionTemplate(reformattedFormData);
      } catch {
        currentQ.formattedLabel = currentQ.warningMessage;
      }

      return currentQ;
    },
    filteredQuestions() {
      return this.questions.filter((question) => this.calculateFieldVisibility(question));
    },
  },

  watch: {
    form: {
      deep: true,
      immediate: true,
      handler() {
        this.$emit('custom-form:submit-payload-changed', this.formatPayload(this.form));
      },
    },
  },

  methods: {
    isCurrentQuestionTheFirstQuestion(currentQuestion) {
      // console.log(currentQuestion.id, this.questions[0].id);

      return currentQuestion.id === this.questions[0].id;
    },
    resolveField(question) {
      /* eslint-disable no-else-return */
      if (question.type === 'checkbox') {
        return CheckboxField;
      } else if (question.type === 'checkboxGroup') {
        return CheckboxGroupField;
      } else if (question.type === 'email') {
        return EmailField;
      } else if (question.type === 'select') {
        return SelectField;
      } else if (question.type === 'radio') {
        return RadioField;
      } else if (question.type === 'label') {
        return LabelField;
      } else if (question.type === 'text' && question.subtype === 'multiline') {
        return TextareaField;
      } else if (question.type === 'number') {
        return NumberField;
      } else if (question.type === 'tel') {
        return TelephoneField;
      } else if (question.type === 'autocomplete') {
        return AutocompleteField;
      } else if (question.type === 'date' && question.subtype === 'date') {
        return DateField;
      } else if (question.type === 'date' && question.subtype === 'time') {
        return TimeField;
      } else if (question.type === 'rating') {
        return RatingField;
      } else if (question.type === 'text') {
        return TextField;
      } else if (question.type === 'multi_choice_image') {
        return MultiChoiceImageField;
      } else if (question.type === 'statement') {
        return StatementField;
      } else if (question.type === 'url') {
        return UrlField;
      } else if (question.type === 'ranking') {
        return RankingField;
      } else if (question.type === 'file_upload') {
        return FileUploadField;
      }

      /* eslint-enable no-else-return */

      return TextField;
    },

    /**
     * The lodash _isEmpty doesnt necessary work as I (Bruno) needed so went with a more "detailed" approach.
     *
     * @param answer
     * @returns {boolean|boolean}
     */
    isVariableEmpty(answer) {
      return answer === undefined
        || answer === null
        || Number.isNaN(answer)
        || (typeof answer === 'object' && Object.keys(answer).length === 0)
        || (typeof answer === 'string' && answer.trim().length === 0);
    },
    hasConditionQuestionsFlow() {
      return this.config.questionsFlow !== undefined && this.config.questionsFlow.length > 0;
    },
    /**
     * The concept of conditional flow (or screen flow) is when based on a given answer the user go to a specific question.
     * Think of a conditional flow as a transition from question A to question B when the answer of A matches a given criteria.
     * The idea here is:
     *  - check if the question has been answered
     *  - loop through the possible transitions
     *  - when one transition match then go to the defined question (eg from question 1 to 4)
     *  - otherwise just go to the default next question (eg: from question 1 to 2)
     */
    resolveNextQuestionBasedOnConditionalFlow() {
      const currentQuestion = this.filteredQuestions[this.formState.currentQuestion];
      const answer = this.form[currentQuestion.id];
      const { questionsFlow } = this.config;

      // eslint-disable-next-line no-plusplus
      for (let index = 0; index < questionsFlow.length; index++) {
        const {
          fromWebFormQuestionId,
          whenCondition,
          whenValue,
          goToWebFormQuestionId,
          endAndSubmitForm,
        } = questionsFlow[index];

        const isFlowRelatedToCurrentQuestion = fromWebFormQuestionId === currentQuestion.id;
        let isFlowConditionAMatch = false;

        switch (whenCondition) {
          case 'equals':
            isFlowConditionAMatch = (whenValue === answer);
            break;
          case 'notEquals':
            isFlowConditionAMatch = (whenValue !== answer);
            break;
          case 'contains':
            isFlowConditionAMatch = (Array.isArray(whenValue) && whenValue.indexOf(answer) > -1);
            break;
          case 'notContains':
            isFlowConditionAMatch = (Array.isArray(whenValue) && whenValue.indexOf(answer) === -1);
            break;
          case 'greaterThan':
            isFlowConditionAMatch = (answer > whenValue);
            break;
          case 'lessThan':
            isFlowConditionAMatch = (answer < whenValue);
            break;
          case 'hasAnswer':
            isFlowConditionAMatch = !this.isVariableEmpty(answer);
            break;
          case 'notAnswer': isEmpty(answer);
            isFlowConditionAMatch = this.isVariableEmpty(answer);
            break;
          case 'default':
            isFlowConditionAMatch = true;

            break;
          default:
            isFlowConditionAMatch = false;
        }

        if (isFlowRelatedToCurrentQuestion && isFlowConditionAMatch && endAndSubmitForm) {
          this.handlFormSubmission();
          return;
        }

        if (isFlowRelatedToCurrentQuestion && isFlowConditionAMatch && !endAndSubmitForm) {
          const goToQuestionIndex = this.filteredQuestions.findIndex((question) => question.id === goToWebFormQuestionId);

          this.$nextTick(() => {
            // The minus one is because this.formState.currentQuestion starts as 0
            this.formState.currentQuestion = goToQuestionIndex;
            this.formState.animationDirection = 'next';
          });
          return;
        }
      }

      // There wasn't a match of any type

      this.$nextTick(() => {
        this.formState.currentQuestion += 1;
        this.formState.animationDirection = 'next';
      });
    },
    preFillQuestion(queryParams, question) {
      // We only pre-fill student questions though we will review this behaviour

      if (!question.isStudentQuestion) {
        return null;
      }

      if (!queryParams.has(question.id)) {
        return null;
      }

      let answer = null;

      switch (question.type) {
        case 'autocomplete':
        case 'select':
          if (question.multiSelect) {
            answer = queryParams.get(question.id) !== null ? queryParams.get(question.id).split(',') : null;
          }

          if (!question.multiSelect) {
            answer = queryParams.get(question.id);
          }

          break;
        case 'checkboxGroup':
          answer = queryParams.get(question.id) !== null ? queryParams.get(question.id).split(',') : null;
          break;
        case 'checkbox':
          answer = queryParams.get(question.id) === 'true';
          break;
        default:
          answer = queryParams.get(question.id);
      }
      return answer;
    },
    /**
     *
     * @param clickedButton The button that was clicked (eg: Next)
     * @returns {boolean|default.methods}
     */
    handlFormSubmission() {
      if (this.loading) {
        return false;
      }
      const payload = this.formatPayload(this.form);
      return this.$emit('custom-form:submit', { payload });
    },

    formatPayload(form) {
      return Object.keys(form)
        .filter((field) => {
          return this.form[field] !== null;
        }).reduce((acc, field) => {
          acc[field] = this.form[field];
          return acc;
        }, {});
    },

    calculateFieldVisibility(question) {
      return question.hidden !== true;
    },

    handleNextQuestionNavigation() {
      const isCurrentQuestionTheLastQuestion = this.formState.currentQuestion >= (this.filteredQuestions.length - 1);

      if (isCurrentQuestionTheLastQuestion) {
        this.handlFormSubmission();

        return;
      }

      if (this.hasConditionQuestionsFlow()) {
        this.resolveNextQuestionBasedOnConditionalFlow();
        return;
      }

      this.$nextTick(() => {
        this.formState.currentQuestion += 1;
        this.formState.animationDirection = 'next';
      });
    },

    scrollToFirstFieldWithError() {
      const element = document.querySelector('.RMPE-HasError');

      if (element === null) return;

      const topOffset = 30;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition - topOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    },
    goBackToFirstQuestion() {
      this.$nextTick(() => {
        this.formState.currentQuestion = 0;
        this.formState.animationDirection = 'previous';
      });
    },
  },

  validations() {
    const formStructure = this.questions
      .filter(question => question.type !== 'label' && question.type !== 'statement')
      .map((question) => {
        // Fields are only required if visible and set as required. this stops conditional fields breaking validation logic.
        let validationRules = question.required === true && this.calculateFieldVisibility(question) ? { required } : {};

        if (question.type === 'email') {
          validationRules = { ...validationRules, email };
        } else if (question.id === 'fullName') {
          validationRules = { ...validationRules, fullName };
        } else if (question.type === 'number') {
          validationRules = { ...validationRules, numeric: decimal };
        } else if (question.type === 'url') {
          validationRules = { ...validationRules, url };
        } else if (question.type === 'file_upload') {
          const maxUploadedFileSize = maxUploadedFileSizeValidator(question.options.maxFileSizeInBytes);
          const allowedFileMimeTypes = mimeTypeUploadedFileValidator(question.options.allowedMimeTypes, question.options.allowedExtensions);
          validationRules = {
            ...validationRules, maxUploadedFileSize, allowedFileMimeTypes,
          };
        }

        question.validationRules = validationRules;

        return question;
      })
      .reduce((carry, question) => {
        carry[question.id] = question.validationRules;
        delete question.validationRules;

        return carry;
      }, {});

    // console.log(formStructure);

    return { form: formStructure };
  },
};
</script>

<style  lang="scss">
// animation to tidy up to fix

.previous-enter-active,
.previous-leave-active,
.next-leave-active,
.next-enter-active {
  position: fixed;
  width: 100%;
  //background: white;
  min-height: 100vh;
  transition: transform 600ms ease 0s, opacity 350ms ease 0s;
  top: 0;
  //opacity: 0; causing issues - be fix
  img{
    opacity: 0;
    transition: opacity 400ms ease-in-out;
  }
}
// router view
.next-enter-active {
  z-index: 2;
  transform: translateY(100%);
}
.next-enter-to {
  z-index: 2;
  opacity: 1;
  transform: translateY(0%);
}

.next-leave-active {
  transform: translateY(-100%);
}

.RMPE-DynamicField-Media,
.RMPE-Layout-ImageLeft,
.RMPE-Layout-ImageRight {
  img {
    transition: opacity 400ms ease-in-out;
  }
}
// router view back
.previous-enter-active {
  z-index: 2;
  transform: translateY(-100%);
}

.previous-enter-to {
  z-index: 2;
  transform: translateY(0);
}

.previous-leave-active {
  z-index: 2;
  transform: translateY(100%);
  img{
    opacity: 0;
  }
}
</style>
<style scoped lang="scss">
.RMPE-CustomForm {

}

.RMPE-CustomOptIn {
  padding: 30px;
  margin-top: 20px;
  margin-bottom: 20px;
  color: #FFF;
  position: relative;
  //background-color: var(--form-font-color);
}

.RMPE-CustomOptIn-CloseIcon {
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
  font-size: 24px;
}
.RMPE-CustomOptIn-Content {
  white-space: pre-wrap;
  background: transparent;
  padding: 0;
  color: #FFF;
  font-family: inherit;
}

.DynamicForm-ResetButton {
  position: fixed;
  //border: 1px solid rgba(0, 0, 0, 0.1);
  //background-color: rgba(255, 255, 255, 0.2);
  //backdrop-filter: blur(2px);
  z-index:6;
  left: 0;
  right: 0;
  padding: 16px;
  bottom: 0;
}

</style>
